import React from "react";
import { Lock, Ruler, Check, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ContourSettings = ({
    canViewContours,
    contourInterval,
    setContourInterval,
    contourTotalLength,
    setContourTotalLength,
    isLoading,
    forceRefresh,
    setForceRefresh
}) => {
    const navigate = useNavigate();

    return (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm space-y-5 relative overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                        <Layers className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Topography Settings</h3>
                        <p className="text-[10px] text-slate-400">Configure spatial contour details</p>
                    </div>
                </div>
            </div>

            {/* Lock Overlay */}
            {!canViewContours && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-50/50 backdrop-blur-[2.5px] p-6 text-center transition-all duration-300">
                    <div className="bg-white border border-slate-200/80 shadow-xl rounded-2xl p-5 max-w-[280px] flex flex-col items-center gap-3 transform scale-100 transition-all hover:scale-[1.01] duration-300">
                        <div className="w-11 h-11 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-600 shadow-inner">
                            <Lock className="w-4 h-4" />
                        </div>
                        <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wide">Unlock Topography Settings</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                            Upgrade to Professional to unlock LiDAR-derived 2ft contour details, slope profiles, and manual mapping overrides.
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/payment")}
                            className="w-full mt-1 bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] text-white py-2 px-4 rounded-xl text-xs font-bold transition-all shadow-md shadow-indigo-600/10 flex items-center justify-center gap-1.5 pointer-events-auto cursor-pointer"
                        >
                            View Upgrade Plans
                        </button>
                    </div>
                </div>
            )}

            {/* Content Wrapper */}
            <div className={`${!canViewContours ? "select-none pointer-events-none filter blur-[0.5px] opacity-40" : ""} space-y-5`}>
                <div className="flex flex-col md:flex-row gap-6 items-stretch">
                    {/* Contour Interval Options */}
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                            Contour Interval (ft)
                        </label>
                        <div className="grid grid-cols-2 gap-3.5">
                            {/* 2ft Option */}
                            <button
                                type="button"
                                onClick={() => canViewContours && setContourInterval(2)}
                                disabled={isLoading || !canViewContours}
                                className={`group flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${contourInterval === 2
                                        ? "border-indigo-600 bg-indigo-50/10 text-slate-900 shadow-sm shadow-indigo-500/5"
                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50/50 hover:border-slate-300"
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Custom Checkbox */}
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${contourInterval === 2
                                            ? "border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-500/20"
                                            : "border-slate-300 bg-white group-hover:border-slate-400"
                                        }`}>
                                        {contourInterval === 2 && (
                                            <Check className="w-3.5 h-3.5 stroke-[3.5]" />
                                        )}
                                    </div>
                                    <div className="flex flex-col items-start text-left min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-bold text-slate-800">2 ft</span>
                                            <span className={`text-[8px] font-extrabold px-1 py-0.5 rounded tracking-wide ${contourInterval === 2 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                                                }`}>HIGH-RES</span>
                                        </div>
                                        <span className="text-[10px] text-slate-400 font-medium">Precision LiDAR</span>
                                    </div>
                                </div>
                                {/* Visual Dense Waves SVG */}
                                <div className="shrink-0 pl-1">
                                    <svg className={`w-8 h-6 transition-all duration-200 ${contourInterval === 2 ? "opacity-100 text-indigo-500 scale-105" : "opacity-40 text-slate-400"
                                        }`} viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 10C25 8 40 25 60 12C80 -1 95 15 95 15" stroke="currentColor" strokeWidth="2.5" />
                                        <path d="M5 25C25 23 40 40 60 27C80 14 95 30 95 30" stroke="currentColor" strokeWidth="2.5" />
                                        <path d="M5 40C25 38 40 55 60 42C80 29 95 45 95 45" stroke="currentColor" strokeWidth="2.5" />
                                        <path d="M5 55C25 53 40 70 60 57C80 44 95 60 95 60" stroke="currentColor" strokeWidth="2.5" />
                                    </svg>
                                </div>
                            </button>

                            {/* 5ft Option */}
                            <button
                                type="button"
                                onClick={() => canViewContours && setContourInterval(5)}
                                disabled={isLoading || !canViewContours}
                                className={`group flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-200 ${contourInterval === 5
                                        ? "border-indigo-600 bg-indigo-50/10 text-slate-900 shadow-sm shadow-indigo-500/5"
                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50/50 hover:border-slate-300"
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <div className="flex items-center gap-3">
                                    {/* Custom Checkbox */}
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${contourInterval === 5
                                            ? "border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-500/20"
                                            : "border-slate-300 bg-white group-hover:border-slate-400"
                                        }`}>
                                        {contourInterval === 5 && (
                                            <Check className="w-3.5 h-3.5 stroke-[3.5]" />
                                        )}
                                    </div>
                                    <div className="flex flex-col items-start text-left min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="text-sm font-bold text-slate-800">5 ft</span>
                                            <span className={`text-[8px] font-extrabold px-1 py-0.5 rounded tracking-wide ${contourInterval === 5 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'
                                                }`}>STANDARD</span>
                                        </div>
                                        <span className="text-[10px] text-slate-400 font-medium">General Feasibility</span>
                                    </div>
                                </div>
                                {/* Visual Spaced Waves SVG */}
                                <div className="shrink-0 pl-1">
                                    <svg className={`w-8 h-6 transition-all duration-200 ${contourInterval === 5 ? "opacity-100 text-indigo-500 scale-105" : "opacity-40 text-slate-400"
                                        }`} viewBox="0 0 100 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M5 15C25 10 40 30 60 15C80 0 95 20 95 20" stroke="currentColor" strokeWidth="2.5" />
                                        <path d="M5 45C25 40 40 60 60 45C80 30 95 50 95 50" stroke="currentColor" strokeWidth="2.5" />
                                    </svg>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block w-px bg-slate-100 self-stretch my-1" />
                    <div className="block md:hidden h-px bg-slate-100 w-full" />

                    {/* Manual Override Input */}
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                            Contour Total Length (ft) <span className="text-slate-400 font-normal ml-1 font-sans capitalize">(Optional Override)</span>
                        </label>
                        <div className="relative rounded-xl border border-slate-200 focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-500/10 transition-all bg-slate-50/40 hover:bg-slate-50/70 focus-within:bg-white flex items-center px-3">
                            <Ruler className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
                            <input
                                type="number"
                                value={contourTotalLength}
                                onChange={(e) => canViewContours && setContourTotalLength(e.target.value)}
                                disabled={isLoading || !canViewContours}
                                placeholder={!canViewContours ? "Pro Feature Locked" : "e.g., 45000"}
                                className="w-full py-3 text-slate-900 placeholder-slate-400 outline-none text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-0 focus:ring-0 p-0"
                            />
                            {contourTotalLength ? (
                                <button
                                    type="button"
                                    onClick={() => setContourTotalLength("")}
                                    className="text-slate-400 hover:text-slate-600 transition-colors p-1"
                                >
                                    <span className="text-lg font-bold">&times;</span>
                                </button>
                            ) : (
                                <span className="text-[9px] font-bold text-slate-400 bg-slate-200/80 px-1.5 py-0.5 rounded uppercase shrink-0">ft</span>
                            )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-2 italic">
                            * Leave blank to use automated spatial measurements computed by the engine.
                        </p>
                    </div>
                </div>

                {/* Minimal Force Refresh Option */}
                <div className="flex items-center gap-2.5 pt-1.5 border-t border-slate-100">
                    <button
                        type="button"
                        id="forceRefreshToggle"
                        disabled={isLoading}
                        onClick={() => setForceRefresh(!forceRefresh)}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${forceRefresh
                                ? "border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-600/10"
                                : "border-slate-300 bg-white hover:border-slate-400"
                            } disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer`}
                    >
                        {forceRefresh && (
                            <Check className="w-2.5 h-2.5 stroke-[4.5]" />
                        )}
                    </button>
                    <label
                        htmlFor="forceRefreshToggle"
                        onClick={() => !isLoading && setForceRefresh(!forceRefresh)}
                        className="text-xs font-semibold text-slate-600 cursor-pointer select-none hover:text-slate-800 transition-colors flex items-center gap-1.5"
                    >
                        Force Fresh Analysis <span className="text-[10px] text-slate-400 font-normal">(Recalculate &amp; bypass cache)</span>
                    </label>
                </div>
            </div>
        </div>
    );
};

export default ContourSettings;
